import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    makeStyles,
    TextField,
    Theme,
    Typography
} from '@material-ui/core'
import React from 'react'
import { PPLFormData, PPLFormErrors } from './ManageUnmappedProductWizard'

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            width: pxToRem(500),
        },
    },
    dialogContent: {
        padding: theme.spacing(0, 3, 2),
    },
    subtitle: {
        fontSize: pxToRem(22),
        fontWeight: 500,
        marginBottom: theme.spacing(2),
        color: '#304959',
    },
    formLabel: {
        minWidth: pxToRem(150),
        fontSize: pxToRem(14),
        marginBottom: theme.spacing(1),
        color: '#304959',
        alignItems: 'center',
        marginRight: theme.spacing(1),
        whiteSpace: 'nowrap'
    },
    formControlWrapper: {
        display: "flex",
        width: '100%',
        alignItems: "center"
    },
    textField: {
        marginBottom: theme.spacing(2),
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#E0E0E0',
            },
            '&:hover fieldset': {
                borderColor: '#1899D4',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1899D4',
            },
            '&.Mui-error fieldset': {
                borderColor: theme.palette.error.main,
            },
        },
        '& .MuiInputLabel-outlined': {
            fontSize: pxToRem(14),
        },
        '& .MuiOutlinedInput-input': {
            fontSize: pxToRem(16),
        },
        '& .MuiFormHelperText-root': {
            marginLeft: 0,
            marginRight: 0,
        },
    },
    textFieldLabel: {
        fontSize: pxToRem(14),
        alignItems: 'center',
    }
}))

/**
 * Validates family number format
 * Must be 6 digits for the main number, optionally followed by a decimal and up to 2 more digits
 * @param value - The family number to validate
 * @returns Whether the family number is valid
 */
const validateFamilyNumber = (value: string): boolean => {
    const isNumbersOnly = new RegExp(/[^0-9]+/, 'g')
    const familyNumber = value?.split('.')
    return (
        familyNumber[0]?.trim()?.replace(isNumbersOnly, '').length === 6 &&
        (!familyNumber[1] ||
            familyNumber[1]?.trim()?.replace(isNumbersOnly, '').length <= 2)
    )
}


/**
 * Props for the CreateNewFamily component
 */
export interface CreateNewFamilyProps {
    /** Set whether the user can continue the process  */
    setIsFormValid: (isValid: boolean) => void
    formData: PPLFormData,
    setFormData: React.Dispatch<React.SetStateAction<PPLFormData>>
    errors: PPLFormErrors
    setErrors: React.Dispatch<React.SetStateAction<PPLFormErrors>>
}

/**
 * Dialog for creating a new family for an unmapped product.
 * 
 * Collects the required information to create a new MasterSpec family:
 * - Section Name
 * - Section Number  
 * - Product Type Name
 * 
 * Includes form validation to ensure all fields are filled before allowing submission.
 * 
 * @param props - The component props
 * @returns The CreateNewFamily component
 */
const CreateNewFamily: React.FC<CreateNewFamilyProps> = ({
    setIsFormValid,
    formData,
    setFormData,
    errors,
    setErrors
}) => {
    const classes = useStyles()

    const handleInputChange = (field: keyof PPLFormData) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {

        setFormData(prev => ({
            ...prev,
            [field]: event.target.value,
        }))

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined,
            }))
        }
    }

    const handleFamilyNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Only allow numbers and decimal points
        const tempValue = event.target.value?.replace(/[^0-9\.]/g, '') ?? ''
        const isFamilyNumberValid = validateFamilyNumber(tempValue)

        setFormData(prev => ({
            ...prev,
            sectionNumber: tempValue,
        }))

        // Set or clear validation error
        if (tempValue && !isFamilyNumberValid) {
            setErrors(prev => ({
                ...prev,
                sectionNumber: 'Invalid Family Number. Must be 6 digits, optionally followed by a decimal and up to 2 more digits.',
            }))
        } else if (errors.sectionNumber && isFamilyNumberValid) {
            setErrors(prev => ({
                ...prev,
                sectionNumber: undefined,
            }))
        }
    }


    const isFormValid = React.useMemo(() => {
        // Check that all fields are filled and there are no validation errors
        const hasAllFields = Boolean(
            formData.sectionName.trim() &&
            formData.sectionNumber.trim() &&
            formData.productTypeName.trim()
        )

        const hasNoErrors = !errors.sectionName && !errors.sectionNumber && !errors.productTypeName
        const hasValidFamilyNumber = validateFamilyNumber(formData.sectionNumber)

        return hasAllFields && hasNoErrors && hasValidFamilyNumber
    }, [formData, errors])

    React.useEffect(() => {
        setIsFormValid(isFormValid)
    }, [isFormValid, setIsFormValid])

    React.useEffect(() => {
        setFormData(prev => ({
            ...prev,
            sectionName: '',
            sectionNumber: '',
            productTypeName: '',
        }))
        setErrors({})
    }, [setFormData, setErrors])

    return (
        <>
            <div className={classes.subtitle}>
                Create a New Family
            </div>

            <Typography className={classes.formControlWrapper}>
                <Typography className={classes.formLabel}>Family Name:</Typography>
                <TextField
                    variant="outlined"
                    fullWidth
                    value={formData.sectionName}
                    onChange={handleInputChange('sectionName')}
                    className={classes.textField}
                    size="small"
                />
            </Typography>

            <Typography className={classes.formControlWrapper}>
                <Typography className={classes.formLabel}>Family Number:</Typography>
                <TextField
                    fullWidth
                    variant="outlined"
                    value={formData.sectionNumber}
                    onChange={handleFamilyNumberChange}
                    error={!!errors.sectionNumber}
                    helperText={errors.sectionNumber}
                    className={classes.textField}
                    size="small"
                />
            </Typography>

            <Typography className={classes.formControlWrapper}>
                <Typography className={classes.formLabel}>Product Type Name:</Typography>
                <TextField
                    fullWidth
                    variant="outlined"
                    value={formData.productTypeName}
                    onChange={handleInputChange('productTypeName')}
                    className={classes.textField}
                    size="small"
                />
            </Typography></>
    )
}

export default CreateNewFamily